import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import { supabase, Shipment, ShipmentUpdate } from '../../../utils/supabase';
import { useSEO } from '../../../utils/seo';

export default function CustomerTracking() {
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = useState('');
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [updates, setUpdates] = useState<ShipmentUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://tracking.ibxshipping.com';

  // SEO Configuration
  useSEO({
    title: 'Track Your Shipment - IBX Shipping Real-Time Tracking',
    description: 'Track your IBX Shipping shipment in real-time. Enter your tracking number to get detailed updates on your cargo location, delivery status, and estimated arrival time.',
    keywords: 'track shipment, shipment tracking, cargo tracking, IBX tracking, real-time tracking, delivery status, package tracking',
    canonical: `${siteUrl}/track`,
    ogType: 'website',
    ogImage: `${siteUrl}/og-image.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/track#webpage`,
          "url": `${siteUrl}/track`,
          "name": "Track Your Shipment - IBX Shipping",
          "description": "Track your IBX Shipping shipment in real-time with detailed status updates",
          "isPartOf": {
            "@id": `${siteUrl}/#website`
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Track Shipment",
                "item": `${siteUrl}/track`
              }
            ]
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What Is a Tracking Number & Where Can I Find It?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A tracking number or ID is a combination of numbers and possibly letters that uniquely identifies your shipment for national or international tracking. Usually, the shipper or online shop is able to provide the tracking number or ID. If you have ordered a product in an online shop, the confirmation email or shipment tracking notification often contains the tracking number or ID. If not, please contact your shipper or online shop."
              }
            },
            {
              "@type": "Question",
              "name": "When will my tracking information appear?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tracking events usually appear 24-48 hours after receiving the Track and Trace ID. In general, once the shipment has reached our facility, a tracking event will appear."
              }
            },
            {
              "@type": "Question",
              "name": "Why is my tracking number/ID not working?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Please make sure you entered the correct tracking number in the correct format: Check for minimum length of 5 characters, and if there are any special characters in your input. Tab, comma, space and semicolon are understood as separators between several tracking IDs. If your tracking ID is not working, please contact your shipper or online shop."
              }
            },
            {
              "@type": "Question",
              "name": "If I do not have my tracking number, is it still possible to track my shipment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "If you do not have a tracking number, we advise you to contact your shipper. However, if you have other shipping reference numbers, they may work using shipment tracking systems of the specific business unit in charge of the shipment."
              }
            }
          ]
        }
      ]
    }
  });

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!trackingId.trim()) {
      alert('Please enter a tracking ID');
      return;
    }

    try {
      setLoading(true);
      setSearched(true);
      
      // Fetch shipment details
      const { data: shipmentData, error: shipmentError } = await supabase
        .from('shipments')
        .select('*')
        .eq('tracking_id', trackingId.trim().toUpperCase())
        .single();

      if (shipmentError) {
        setShipment(null);
        setUpdates([]);
        return;
      }

      setShipment(shipmentData);

      // Fetch updates
      const { data: updatesData, error: updatesError } = await supabase
        .from('shipment_updates')
        .select('*')
        .eq('tracking_id', trackingId.trim().toUpperCase())
        .order('updated_at', { ascending: true });

      if (updatesError) throw updatesError;
      setUpdates(updatesData || []);
    } catch (error) {
      console.error('Error tracking shipment:', error);
      setShipment(null);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Created': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'At Port': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'In Transit': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Out for Delivery': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Delivered': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Created': return 'ri-file-add-line';
      case 'At Port': return 'ri-ship-line';
      case 'In Transit': return 'ri-truck-line';
      case 'Out for Delivery': return 'ri-roadster-line';
      case 'Delivered': return 'ri-checkbox-circle-fill';
      default: return 'ri-information-line';
    }
  };

  const getProgressPercentage = () => {
    if (!updates.length) return 0;
    
    const statusOrder = ['Created', 'At Port', 'In Transit', 'Out for Delivery', 'Delivered'];
    const latestStatus = updates[updates.length - 1]?.status;
    const statusIndex = statusOrder.indexOf(latestStatus);
    
    return statusIndex >= 0 ? ((statusIndex + 1) / statusOrder.length) * 100 : 0;
  };

  const getWeightUnit = (modeOfTransportation?: string) => {
    return modeOfTransportation === 'Air' ? 'kg' : 'cbm';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8)), url('https://readdy.ai/api/search-image?query=Modern%20shipping%20container%20port%20with%20cargo%20ships%20and%20cranes%20at%20sunset%2C%20professional%20logistics%20and%20transportation%20background%20with%20blue%20and%20orange%20sky%2C%20clean%20minimal%20industrial%20scene&width=1200&height=400&seq=hero-tracking&orientation=landscape')`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="mb-6">
            <a
              href="https://ibxshipping.com/"
              className="inline-flex items-center justify-center font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:border-blue-300 disabled:text-blue-300 px-4 py-2 text-base bg-white/10 border-white/20 text-white hover:bg-white/20 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Homepage
            </a>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Track Your Shipment
          </h1>
          <p className="text-xl text-blue-100 mb-8">
            Enter your tracking number below to view your current shipment status
          </p>
          
          <Card className="max-w-md mx-auto">
            <form onSubmit={handleTrack} className="space-y-4">
              <Input
                label="Tracking ID"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                placeholder="Enter tracking ID (e.g., IBX123456)"
                className="text-center text-lg font-mono"
              />
              <Button
                type="submit"
                loading={loading}
                className="w-full"
                size="lg"
              >
                <i className="ri-search-line mr-2"></i>
                Track Shipment
              </Button>
            </form>
          </Card>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Results Section */}
        {searched && (
          <>
            {shipment ? (
              <div className="space-y-8">
                {/* Shipment Summary */}
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Shipment Details</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(shipment.status)}`}>
                      {shipment.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Tracking ID</p>
                      <p className="text-lg font-mono font-semibold text-gray-900">{shipment.tracking_id}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Origin</p>
                      <p className="text-gray-900">{shipment.origin}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">Destination</p>
                      <p className="text-gray-900">{shipment.destination}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">
                        {shipment.mode_of_transportation === 'Air' ? 'Weight' : 'Volume'}
                      </p>
                      <p className="text-gray-900">{shipment.weight} {getWeightUnit(shipment.mode_of_transportation)}</p>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <p className="text-sm font-medium text-gray-600 mb-1">Last Updated</p>
                    <p className="text-gray-900">{new Date(shipment.updated_at).toLocaleString()}</p>
                  </div>
                  
                  {shipment.remarks && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-1">Remarks</p>
                      <p className="text-blue-800">{shipment.remarks}</p>
                    </div>
                  )}
                </Card>

                {/* Progress Bar */}
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Progress</h3>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${getProgressPercentage()}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      {Math.round(getProgressPercentage())}% Complete
                    </p>
                  </div>
                </Card>

                {/* Timeline */}
                <Card>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Shipment Timeline</h3>
                  
                  <div className="relative space-y-6">
                    {updates.map((update, index) => (
                      <div key={update.id} className="relative flex items-start space-x-4">
                        {/* Timeline line */}
                        {index < updates.length - 1 && (
                          <div className="absolute left-4 top-8 w-0.5 h-full bg-gray-200"></div>
                        )}
                        
                        {/* Status icon */}
                        <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${getStatusColor(update.status)}`}>
                          <i className={`${getStatusIcon(update.status)} text-sm`}></i>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {update.status}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {new Date(update.updated_at).toLocaleDateString()} at{' '}
                              {new Date(update.updated_at).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-1">{update.location}</p>
                          {update.remarks && (
                            <p className="text-sm text-gray-500">{update.remarks}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {updates.length === 0 && (
                      <div className="text-center py-8">
                        <i className="ri-time-line text-3xl text-gray-400 mb-2"></i>
                        <p className="text-gray-500">No tracking updates available yet</p>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            ) : (
              <Card>
                <div className="text-center py-12">
                  <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No Record Found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    No shipment found for tracking ID "{trackingId}". Please verify and try again.
                  </p>
                  <Button onClick={() => {
                    setTrackingId('');
                    setSearched(false);
                  }}>
                    Try Again
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Information Section */}
        {!searched && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <Card className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Tracking</h3>
              <p className="text-gray-600">
                Simply enter your tracking ID to get real-time updates on your shipment status.
              </p>
            </Card>
            
            <Card className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Updates</h3>
              <p className="text-gray-600">
                Get detailed timeline of your shipment journey from origin to destination.
              </p>
            </Card>
            
            <Card className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Reliable</h3>
              <p className="text-gray-600">
                Your shipment information is secure and tracking data is always up-to-date.
              </p>
            </Card>
          </div>
        )}
      </main>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Find answers to common questions about tracking your shipments
            </p>
          </div>

          <div className="space-y-8">
            {/* FAQ Item 1 */}
            <Card className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">1</span>
                What Is a Tracking Number & Where Can I Find It?
              </h3>
              <p className="text-gray-700 leading-relaxed ml-12">
                A tracking number or ID is a combination of numbers and possibly letters that uniquely identifies your shipment for national or international tracking.
              </p>
              <p className="text-gray-700 leading-relaxed ml-12 mt-3">
                Usually, the shipper or online shop is able to provide the tracking number or ID. If you have ordered a product in an online shop, the confirmation email or shipment tracking notification often contains the tracking number or ID. If not, please contact your shipper or online shop.
              </p>
            </Card>

            {/* FAQ Item 2 */}
            <Card className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">2</span>
                When will my tracking information appear?
              </h3>
              <p className="text-gray-700 leading-relaxed ml-12">
                Tracking events usually appear 24-48 hours after receiving the Track and Trace ID. In general, once the shipment has reached our facility, a tracking event will appear.
              </p>
            </Card>

            {/* FAQ Item 3 */}
            <Card className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">3</span>
                Why is my tracking number/ID not working?
              </h3>
              <p className="text-gray-700 leading-relaxed ml-12">
                Please make sure you entered the correct tracking number in the correct format: Check for minimum length of 5 characters, and if there are any special characters in your input. Tab, comma, space and semicolon are understood as separators between several tracking IDs.
              </p>
              <p className="text-gray-700 leading-relaxed ml-12 mt-3">
                If your tracking ID is not working, please contact your shipper or online shop.
              </p>
            </Card>

            {/* FAQ Item 4 */}
            <Card className="p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-start">
                <span className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-4 mt-1 flex-shrink-0">4</span>
                If I do not have my tracking number, is it still possible to track my shipment?
              </h3>
              <p className="text-gray-700 leading-relaxed ml-12">
                If you do not have a tracking number, we advise you to contact your shipper. However, if you have other shipping reference numbers, they may work using shipment tracking systems of the specific business unit in charge of the shipment.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
